import { Component, OnDestroy, OnInit } from '@angular/core';
import { IAvailabilityResponse, IAvailabilityUrlLocation, IPMItem, IRequestItem, ISelectedProduct } from '../../pages/service-request/service-request.interface';
import { IContractData, IFfsItem } from '../../../interfaces/IJob';
import { DataService } from '../../../services/data/data.service';
import { Subject, takeUntil } from 'rxjs';
import { RouterService } from '../../../services/router/router.service';
import { APP_ROUTES } from '../../../utils/route.constant';
import { ServiceRequestService } from '../../../services/service-request/service-request.service';
import { ERROR_MESSAGE_FOR_SELECTED_PRODUCT, FFS_TYPE_DRYER_VENT_CLEANING } from '../../../utils/constants';
import { ACC_LOGGEDIN, ACC_VIVR, ALL_PAGES, DASHBOARD_PAGE_LOCATION, ERR_EVENT, MYACCOUNT_LOGGEDIN_PM, MYACCOUNT_VIVR_PM, START_FFS_FLOW, START_FFS_FLOW_DRYER_VENT_CLEANING } from '../../../services/ga4/ga4.constant';
import { Ga4Service } from '../../../services/ga4/ga4.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PM_MAX_TRIES } from '../../pages/service-request/service-request.constants';
import { IMessage } from '../../../services/messages/messages.interface';

@Component({
  selector: 'app-ffs-banner',
  templateUrl: './ffs-banner.component.html',
  styleUrl: './ffs-banner.component.scss'
})
export class FfsBannerComponent implements OnInit, OnDestroy {
  isPlanFfs: boolean = false;
  contractData!: IContractData;
  isVivr: boolean = false;
  ffsItem!: IPMItem | null;
  maxRetries: number = 10; //
  cleaner$ = new Subject<void>();
  private readonly pollIntervalMs = 3000;
  private pollingTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private dataService: DataService,
    private routerService: RouterService,
    private serviceRequestService: ServiceRequestService,
    private gaService: Ga4Service
  ) { }

  ngOnInit() {
    this.isVivr = this.dataService.isVivr$.getValue();

    this.dataService.contractData$
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (contract) => {
          if (!contract) {
            this.routerService.goto(APP_ROUTES.DASHBOARD);
            return;
          };
          if (this.contractData?.baseContract?.contractNumber !== contract.baseContract.contractNumber) {
            // clear FFS selection when contract changes
            // to load the new one if available
            this.dataService.isPlanFFSSelected$.next(null);
          }
          this.contractData = contract;
          this.isPlanFfs = this.dataService.isPlanFFS();
        },
        error: (error) => {
          console.error('Error retrieving contract data:', error);
          this.routerService.goto(APP_ROUTES.DASHBOARD);
        }
      });
  }

  onBannerContentClick(event: Event, item: IFfsItem): void {
    const target = event.target as HTMLElement;
    // Check if the clicked element or its parent has the 'ffs-navigation' data auto id
    const ffsNavigation = target.closest('[data-auto-id="ffs-navigation"]');
    if (ffsNavigation) {
      event.preventDefault();
      event.stopPropagation();
      this.ga4Tracking(item.serviceName);
      this.navigateToFfs(item);
    }
  }

  ga4Tracking(ffsType: string): void {
    const ffsFlowType = (ffsType.toLowerCase().includes(FFS_TYPE_DRYER_VENT_CLEANING.toLowerCase())) ? START_FFS_FLOW_DRYER_VENT_CLEANING : START_FFS_FLOW;
    this.gaService.buttonClickEvent(DASHBOARD_PAGE_LOCATION, ffsFlowType);
  }

  navigateToFfs(ffsItem: IFfsItem): void {
    if (!this.contractData || !this.isPlanFfs) {
      return;
    }

    this.dataService.globalLoader$.next(true);
    this.serviceRequestService.getPreventiveMaintenanceItems(this.contractData.baseContract.contractNumber, this.isVivr, this.isPlanFfs)
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (response => {
          if (!response.length) {
            this.dataService.globalLoader$.next(false);
            this.routerService.goto(APP_ROUTES.SER_REQ_TECHNICIAN_NOT_FOUND);
            return;
          }

          const selectedPMItem = response.find((item: IPMItem) => item?.serviceName?.toLowerCase() === ffsItem.serviceName.toLowerCase());
          this.ffsItem = selectedPMItem || null;
          this.dataService.serviceRequestPMData$.next(response);
          if (!this.ffsItem) {
            this.dataService.globalLoader$.next(false);
            return;
          }

          const selectedPMData = {
            ...this.ffsItem!,
            "isHover": false,
            "isSelected": true
          };

          const selectedProducts = this.getSelectedProducts(selectedPMData);
          this.dataService.serviceRequestSelectedProducts$.next(selectedProducts);

          let itemsForRequest: IRequestItem[] = [{
            "requestProductLine": this.ffsItem!.categoryId,
            "brand": "",
            "id": this.ffsItem!.categoryId,
          }];
          let postData = {
            'zipCode': this.contractData.baseContract.propertyAddress.zip,
            'contractNumber': this.contractData.baseContract.contractNumber,
            items: itemsForRequest
          };
          this.dataService.isPlanFFSSelected$.next(ffsItem);

          this.serviceRequestService.checkAvailability(this.contractData.baseContract.contractNumber, postData, this.isVivr)
            .pipe(takeUntil(this.cleaner$))
            .subscribe({
              next: (response: IAvailabilityUrlLocation) => {
                if (response && response.Location) {
                  const url = response.Location;
                  this.recursiveCall(url, 0);
                } else {
                  this.dataService.globalLoader$.next(false);
                  this.showErrorMessage({ messages: ERROR_MESSAGE_FOR_SELECTED_PRODUCT });
                }
              },
              error: (err: HttpErrorResponse) => {
                this.dataService.globalLoader$.next(false);
              }
            });
        }),
        error: (error => {
          this.dataService.globalLoader$.next(false);
          console.log(error);
        })
      });
  }

  recursiveCall(url: string, inc: number) {
    if (inc >= this.maxRetries) {
      const msg = PM_MAX_TRIES;
      this.showErrorMessage({ messages: msg });
      return;
    }
    if (this.pollingTimer !== null) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.pollingTimer = setTimeout(() => {
      //  this.isLoading = true;
      this.dataService.globalLoader$.next(true);
      this.serviceRequestService.recursiveGetCall<IAvailabilityResponse | null>(url, this.isVivr)
        .pipe(takeUntil(this.cleaner$))
        .subscribe({
          next: (response: (IAvailabilityResponse | null)) => {
            if (response === null) {
              this.recursiveCall(url, inc + 1);
              return;
            }


            let selectedProductsAvailablityResponse = response;

            if (selectedProductsAvailablityResponse.successFlag === "Y") {
              let items = selectedProductsAvailablityResponse.items;
              let isAvailable = true;
              for (let i = 0; i < items.length; i++) {
                if (items[i].availability.successFlag === "N") {
                  isAvailable = false;
                }
                if (items[i].availability.availabilityFound === "N") {
                  isAvailable = false;
                }
                if (
                  items[i].availability.availabilityFound === "Y" &&
                  items[i].availability.availabilityDates
                ) {
                  if (items[i].availability.availabilityDates.length === 0) {
                    isAvailable = false;
                  }
                  let checkForAvailablityTrue = items[
                    i
                  ].availability.availabilityDates.filter(
                    (x: { available: boolean }) => x.available === true
                  );
                  if (checkForAvailablityTrue.length === 0) {
                    isAvailable = false;
                  }
                }
              }
              if (isAvailable === true) {
                //Save data and navigate to schedule appointment
                this.dataService.serviceRequestAvailablity$.next(response);
                this.routerService.goto(APP_ROUTES.SCHEDULE_APPOINTMENT, { queryParamsHandling: 'merge' });
                this.dataService.globalLoader$.next(false);
              } else {
                // navigate to technician not found page
                this.routerService.goto(APP_ROUTES.SER_REQ_TECHNICIAN_NOT_FOUND);
                this.dataService.globalLoader$.next(false);
              }
            } else {
              this.routerService.goto(APP_ROUTES.SER_REQ_TECHNICIAN_NOT_FOUND);
              this.dataService.globalLoader$.next(false);
            }
          },
          error: (err) => {
            //   this.isLoading = false;
            this.dataService.globalLoader$.next(false);
            this.showErrorMessage({ messages: ERROR_MESSAGE_FOR_SELECTED_PRODUCT });
          }
        });
    }, this.pollIntervalMs);
  }

  showErrorMessage(err: { messages: string; }) {
    if (err) {
      this.dataService.globalLoader$.next(false);
      const errorMessage: IMessage = {
        severity: 'error',
        text: err?.messages ? err?.messages : ERROR_MESSAGE_FOR_SELECTED_PRODUCT,
        icon: 'pi pi-ban'
      };
      this.gaService.messageEvent(ERR_EVENT, err?.messages ? err?.messages : ERROR_MESSAGE_FOR_SELECTED_PRODUCT, ALL_PAGES);
      this.dataService.messagesToDisplay$.next([errorMessage])
    }
  }

  getSelectedProducts(pmData: IPMItem): ISelectedProduct[] {
    const selectedProduct: ISelectedProduct = {
      category: pmData.itemCode,
      categoryId: pmData.categoryId,
      item: pmData.itemDescription,
      itemId: pmData.categoryId,
      location: '',
      brand: '',
      brandName: '',
      model: '',
      locationCode: '',
      otherBrand: '',
      serialNo: '',
      burkeInfo: {
        symptomId: 0,
        legacyCode: '',
        symptomDescription: '',
        productTypeId: '',
        itemId: '',
        modelId: '',
        brandId: 0,
      },
      deductible: 0,
      replacementDeductibleDue: 0,
      eligible: false,
      isEditFlag: false,
      symptoms: [],
      brands: [],
    };
    return [selectedProduct];
  }

  ngOnDestroy(): void {
    if (this.pollingTimer !== null) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.cleaner$.next();
    this.cleaner$.complete();
    this.dataService.globalLoader$.next(false);
  }
}

